import axios from 'axios';
import io from 'socket.io-client';
import { uniqueId, WaitingList, assetUtils } from '@zen/common-utils';

const { fromKalapas } = assetUtils;
// eslint-disable-next-line eqeqeq
const fromKalapasIfExist = (value) => (value || value == 0 ? fromKalapas(value) : value);
const ordersFromKalapas = (data) => ({
  count: data.count,
  items: data.items.map(
    ({ underlyingAmount, orderTotal, requestedPayout, providedAmount, ...order }) => ({
      ...order,
      underlyingAmount: fromKalapasIfExist(underlyingAmount),
      orderTotal: fromKalapasIfExist(orderTotal),
      requestedPayout: fromKalapasIfExist(requestedPayout),
      providedAmount: fromKalapasIfExist(providedAmount),
    })
  ),
});

export default function WebDbClient(config) {
  let chain = config.chain;
  const subscriptions = WaitingList();
  let accessible = false;

  let request = axios.create({
    baseURL: config.orderbookServer[chain],
    timeout: 10000,
  });

  async function sendHttpRequest(config) {
    return request
      .request(config)
      .then((response) => response.data)
      .catch((error) => {
        console.error(`Error fetching data from orderbook server, ${error.message}`);
        throw error;
      });
  }

  async function findContractExecutionsByPkHash({ pkHash } = {}) {
    return sendHttpRequest({
      url: `api/v1/executions/${pkHash}`,
      method: 'get',
    })
      .then(ordersFromKalapas)
      .catch(() => ({ items: [] }));
  }

  async function findOrdersByBaseQuote({ base, quote } = {}) {
    return sendHttpRequest({
      url: 'api/v1/orders',
      method: 'get',
      params: {
        base,
        quote,
      },
    })
      .then(ordersFromKalapas)
      .catch(() => ({ items: [] }));
  }

  async function findTopPairs({ skip, take } = {}) {
    return sendHttpRequest({
      url: 'api/v1/executions/top-markets',
      method: 'get',
      params: { skip, take },
    }).catch(() => ({ count: 0, items: [] }));
  }

  async function findAllInfos() {
    return sendHttpRequest({
      url: 'api/v1/infos',
      method: 'get',
    }).catch(() => ({ items: [] }));
  }

  function handleSettingsChanged(settings) {
    if (settings.chain !== chain) {
      chain = settings.chain;
      setServerUrlByChain(chain);
    }
  }

  /**
   * Map db tables to methods
   */
  const DbTableMethods = {
    Orders: ['findOrdersByBaseQuote'],
    ContractExecutions: ['findContractExecutionsByPkHash', 'findTopPairs'],
    Infos: ['findAllInfos'],
  };
  const AvailableMethods = {
    findOrdersByBaseQuote,
    findContractExecutionsByPkHash,
    findTopPairs,
    findAllInfos,
  };

  async function refresh() {
    // just wait a few seconds for the server to be able to update
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 5000);
    });
  }

  function requestDeleteStorage() {
    // this function is valid on desktop only
  }

  /**
   * Subscribe to a specific method
   * @returns the unique id generated for this subscription, should be used to unsubscribe
   */
  function subscribe({ method, payload, handler } = {}) {
    const id = uniqueId();
    subscriptions.add(id, { method, payload, handler });
    return id;
  }
  function unsubscribe(id) {
    subscriptions.remove(id);
  }

  function getAccessibility() {
    return accessible;
  }

  let socket;
  function initSocket() {
    // first disconnect in case it was already connected
    if (socket && typeof socket.disconnect === 'function') {
      socket.disconnect();
    }

    // start init
    socket = io.connect(config.orderbookServer[chain]);
    socket.on('connect', () => {
      accessible = true;
      console.log('Listening for orderbook db changes');
    });
    socket.on('disconnect', () => {
      accessible = false;
      console.log('Disconnected from orderbook db');
    });
    // run all relevant subscriptions on db-change
    socket.on('db-change', ({ tableName }) => {
      subscriptions
        .getAll()
        .filter((item) => DbTableMethods[tableName].includes(item.method))
        .forEach((subscription) =>
          AvailableMethods[subscription.method](subscription.payload).then(subscription.handler)
        );
    });
  }
  initSocket(); // on first run

  function setServerUrlByChain(chain) {
    request.defaults.baseURL = config.orderbookServer[chain];

    initSocket();
  }

  return {
    getAccessibility,
    findOrdersByBaseQuote,
    findAllInfos,
    findContractExecutionsByPkHash,
    findTopPairs,
    handleSettingsChanged,
    refresh,
    requestDeleteStorage,
    subscribe,
    unsubscribe,
  };
}
